/**
 * EVERY SIGN-IN THE PERSON LOOKING AT THIS BOARD HOLDS, for one question
 * only: are these words hers?
 *
 * Bobbie, 2026-10-01, verbatim: "Only directors can remove other people's
 * posts". An author keeps Take this back on her own words. A Jr Staff is ONE
 * PERSON however she signs in (CLAUDE.md, DECISIONS 26 and 27): a post she
 * wrote from her email staff account is hers on her dancer sign-in too, so
 * the board must offer her Take this back on either, and the server
 * (netlify/functions/boardModeration.mjs, action 'remove') answers the same
 * way through the server half of the same helper (lib/jrStaffAliasUids.mjs).
 *
 * THE DECISION IS jrStaffAliasUidsFor (./jrStaffAliases.ts), not re-spelled
 * here. This file only fetches the accounts it decides over, with the same
 * provable query src/lib/threads.ts's resolveSenderAliasUids already runs:
 * users/ filtered to this studentId, status 'active' and a staff role, which
 * is exactly the slice shared/firestore.rules lets an active staff caller
 * list. A guardian (role 'family') carrying her dancer's studentId never
 * passes isOwnJrStaffSignIn, so she costs nothing and is never widened.
 *
 * NOT A PERMISSION. It only decides which label and which button to draw;
 * the server decides who may remove. A failed or denied query degrades to
 * the uid actually signed in, which is the board's old exact-uid answer.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from './firebase';
import {
  JR_STAFF_ALIAS_MAX,
  JR_STAFF_ALIAS_STAFF_ROLES,
  isOwnJrStaffSignIn,
  jrStaffAliasUidsFor,
  mergeOwnSignInUids,
  type JrStaffAliasAccount,
} from './jrStaffAliases';

/** The fields of the viewer's own users doc this reads. */
export interface OwnSignInDoc {
  role?: string | null;
  status?: string | null;
  studentId?: string | null;
}

/** Is this item's author one of the viewer's own sign-ins? */
export function isOwnBoardWords(authorUid: string | null | undefined, ownUids: readonly string[]): boolean {
  return Boolean(authorUid) && ownUids.includes(String(authorUid));
}

export interface OwnSignInOptions {
  /**
   * The server half of the same helper, for a page whose server can name a
   * sign-in this browser is not allowed to read: a Jr Staff's UNPROMOTED
   * dancer sign-in (status 'dancer'), which the users read rule hides from
   * staff. The class page passes classPosts.ts's loadClassPostOwnSignInUids,
   * the list classPostRecord.mjs itself decides authorship with. Must be a
   * stable function (a module export), not a fresh closure per render.
   */
  askServer?: () => Promise<readonly string[]>;
}

/**
 * The viewer's sign-ins, her own uid always first. Answers [uid] at once and
 * widens only for a Jr Staff whose own account carries a studentId.
 */
export function useOwnSignInUids(
  uid: string | null | undefined,
  ownDoc: OwnSignInDoc | null | undefined,
  options: OwnSignInOptions = {}
): string[] {
  const role = ownDoc?.role ?? null;
  const status = ownDoc?.status ?? null;
  const studentId = ownDoc?.studentId ?? '';
  const eligible = Boolean(uid && studentId
    && isOwnJrStaffSignIn({ uid: uid ?? '', role, status, studentId }));
  const key = eligible ? `${uid}:${studentId}:${role}:${status}` : '';
  const [linked, setLinked] = useState<{ key: string; uids: string[] } | null>(null);

  useEffect(() => {
    if (!eligible || !uid || !db) return;
    let live = true;
    const mine: JrStaffAliasAccount = { uid, role, status, studentId };
    void getDocs(query(
      collection(db, 'users'),
      where('studentId', '==', studentId),
      where('status', '==', 'active'),
      where('role', 'in', [...JR_STAFF_ALIAS_STAFF_ROLES]),
      limit(JR_STAFF_ALIAS_MAX + 1)
    )).then((snap) => {
      if (!live) return;
      const accounts: JrStaffAliasAccount[] = [
        mine,
        ...snap.docs.map((row) => ({ ...(row.data() as JrStaffAliasAccount), uid: row.id })),
      ];
      setLinked({ key, uids: jrStaffAliasUidsFor({ uid, studentId, accounts }) });
    }).catch((error: unknown) => {
      // Her own uid alone, the board's old answer. Said out loud so a rules
      // regression shows up instead of quietly hiding Take this back.
      console.warn('useOwnSignInUids: link lookup failed', error);
    });
    return () => {
      live = false;
    };
  }, [eligible, key, role, status, studentId, uid]);

  const askServer = options.askServer;
  const [fromServer, setFromServer] = useState<{ key: string; uids: string[] } | null>(null);
  useEffect(() => {
    if (!eligible || !uid || !askServer) return;
    let live = true;
    void askServer().then((uids) => {
      if (!live) return;
      setFromServer({ key, uids: [...uids] });
    }).catch((error: unknown) => {
      // Her own uid plus whatever the users query proved, the old answer.
      console.warn('useOwnSignInUids: server lookup failed', error);
    });
    return () => {
      live = false;
    };
  }, [askServer, eligible, key, uid]);

  return useMemo(() => {
    if (!uid) return [];
    return mergeOwnSignInUids(
      uid,
      linked && linked.key === key && key ? linked.uids : null,
      fromServer && fromServer.key === key && key ? fromServer.uids : null
    );
  }, [fromServer, key, linked, uid]);
}
